import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { getConfig } from '@config/runtimeConfig';

export const MOCK = process.env.REACT_APP_USE_MOCK_DATA === 'true';

export const userManager = new UserManager({
  authority: getConfig('OIDC_AUTHORITY'),
  client_id: getConfig('OIDC_CLIENT_ID'),
  redirect_uri: `${window.location.origin}/callback`,
  // Land on the PUBLIC /login page after IdP sign-out. This exact URI
  // must be registered as a post-logout redirect URI on the SPA app registration in Entra.
  post_logout_redirect_uri: `${window.location.origin}/login`,
  response_type: 'code',
  // offline_access makes the refresh token explicit/guaranteed so automaticSilentRenew uses
  // the refresh-token grant (background POST) rather than the fragile hidden-iframe path.
  scope: `openid profile email offline_access ${getConfig('OIDC_API_SCOPE') || ''}`.trim(),
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.sessionStorage })
});

// Set true synchronously right before an RP-initiated logout redirect, and checked by PrivateRoute
// so it does not fire a sign-in during the window after react-oidc-context clears activeNavigator.
// Resets to false naturally on the full page reload when logout returns to /login.
export const signoutState = { inProgress: false };

// A logout redirect can be cancelled with the browser Back button. The app page then comes back
// from the back/forward cache with this module (and the React tree) still in memory: the OIDC user
// is already cleared, signoutState.inProgress is still true so PrivateRoute will not sign in, and
// no effect re-runs on a bfcache restore — the result is a loading screen that never resolves.
// Reload so auth is re-evaluated from scratch (Entra either signs the user back in silently or
// shows its prompt).
window.addEventListener('pageshow', (event) => {
  if (!event.persisted || !signoutState.inProgress) return;
  signoutState.inProgress = false;
  window.location.reload();
});

// Surface silent-renew failures instead of silently degrading to a full-page redirect on the
// next API call. Logger is imported lazily to avoid the userManager -> Logging -> BaseApi ->
// userManager import cycle.
userManager.events.addSilentRenewError((error) => {
  console.error('OIDC silent renew failed:', error);
  import('@api/Logging')
    .then(({ logWarn }) =>
      logWarn('OIDC silent renew failed', error, {
        name: error?.name,
        message: error?.message
      })
    )
    .catch(() => {});
});

export const getAccessToken = async () => {
  if (MOCK) return null;
  const u = await userManager.getUser();
  return u && !u.expired ? u.access_token : null;
};

export const getOidcUserId = async () => {
  if (MOCK) return null;
  const u = await userManager.getUser();
  return u?.profile?.oid || u?.profile?.sub || null;
};
